import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteExternalInvoices } from 'api/Billing';

import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';

import { onSendEmail, onSendText } from '../lib/utils';

import { CustomSendModal } from './contactButton/CustomSendModal';

const SendInvoiceMessagePopUp = ({ data }) => {
  const { id, patient } = data || {};

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [customModalVisible, setCustomModalVisible] = useState(false);
  const [customContactType, setCustomContactType] = useState('email');

  const { mutate: deleteInvoice, isLoading: isDeleteInvoicesLoading } = useMutation({
    mutationFn: (data) => deleteExternalInvoices(navigate, data)
  });

  const onDeleteInvoice = () => {
    deleteInvoice(
      { id },
      {
        onSuccess: (data) => {
          if (data?.deletedInvoice?.deleted) {
            queryClient.invalidateQueries(['external_invoices']);
            showAlert({
              title: 'Invoices deleted',
              color: 'success'
            });
          }
        },
        onError: () => {
          showAlert({
            title: "Couldn't delete invoice",
            message: 'Please try again.',
            color: 'danger'
          });
        }
      }
    );
  };

  const onOpenDeleteDialog = () => {
    setShowConfirmationModal(true);
  };

  const handleCustomSendModalClose = () => {
    setCustomModalVisible(false);
  };

  const handleCustomSendModalOpen = (type) => {
    setCustomContactType(type);
    setCustomModalVisible(true);
  };

  const actionOptions = [
    {
      label: 'Send text',
      children: [
        patient?.phone && {
          label: `${patient?.phone} (primary)`,
          onClick: () => onSendText({ ids: [id], navigate, type: 'primary' }),
          icon: 'message-mono'
        },
        patient?.alt_phone && {
          label: `${patient?.alt_phone} (alternative)`,
          onClick: () => onSendText({ ids: [id], navigate, type: 'alternative' }),
          icon: 'message-mono'
        },
        { label: 'Custom', onClick: () => handleCustomSendModalOpen('text') }
      ].filter(Boolean)
    },
    {
      label: 'Send email',
      children: [
        patient?.email && {
          label: `${patient?.email} (primary)`,
          onClick: () => onSendEmail({ ids: [id], navigate, type: 'primary' }),
          icon: 'mail-mono'
        },
        patient?.alt_email && {
          label: `${patient?.alt_email} (alternative)`,
          onClick: () => onSendEmail({ ids: [id], navigate, type: 'alternative' }),
          icon: 'mail-mono'
        },
        { label: 'Custom', onClick: () => handleCustomSendModalOpen('email') }
      ].filter(Boolean)
    },
    patient?.id && {
      label: 'View patient',
      onClick: () => window.open(`/portal/charts/${patient.id}/billing/invoices`, '_blank'),
      icon: 'user-2-mono'
    },
    {
      label: 'Delete',
      onClick: onOpenDeleteDialog,
      icon: 'trash-mono',
      permission: { requiredPermissions: 'invoicing.delete' }
    }
  ].filter(Boolean);

  return (
    <>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={actionOptions}
        isBorder={false}
      />
      <Confirm
        variant="danger"
        title="Delete external invoice"
        icon="trash-mono"
        message="Are you sure you want to delete this invoice?"
        isOpen={!!showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        footerActions={{
          primary: {
            text: 'Delete',
            onClick: onDeleteInvoice,
            color: 'danger',
            isLoading: isDeleteInvoicesLoading
          }
        }}
      />
      <CustomSendModal
        ids={[id]}
        contactType={customContactType}
        visible={customModalVisible}
        onClose={handleCustomSendModalClose}
        onPrev={handleCustomSendModalClose}
      />
    </>
  );
};

export default SendInvoiceMessagePopUp;
